import {
    ReactElement,
    useState,
} from 'react';

import {
    Center,
    Checkbox,
    SimpleGrid,
    Table,
    Text,
} from '@mantine/core';

import {useRegistrations} from '../hooks/useRegistrations.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    SimpleTablePagination,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';

import PropTypes from 'prop-types';
import {IRegistrationTableColumn, Registration} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {formatTimestamp} from '../../../utils/TableTimestamps';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/RegistrationsService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[],
    selectedRecords: Registration[],
    setSelectedRecords: (records: Registration[]) => void,
}) => {

    const {allowedActions} = useVerifiedPermissions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Registration | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        fetchRecords,
        handleFilterByKeyword,
    } = useRegistrations();

    const handleRecordClick = (record: Registration) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Registration) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as Registration);
            queryClient.invalidateQueries({queryKey: ['registrations']});
        }
    };

    const handleRowSelection = (row: { id: string }) => {
        const exists = selectedRecords.find(r => r.id === row.id);
        if (exists) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(r => r.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const getColumns = () => {
        const cols: IRegistrationTableColumn[] = [];
        if (allowedActions.includes('DeleteRegistration')) {

            cols.push({
                id: 'select',
                title: '',
                render: item => {
                    if (item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername) {
                        return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                    } else {
                        return (<Center><Checkbox disabled={true}></Checkbox></Center>);
                    }
                },
            });
        }

        cols.push({
            title: I18n.get('ID'),
            width: '1fr',
            render: (item: Registration) => (
                <Text
                    c="blue"
                    style={{cursor: 'pointer'}}
                    onClick={() => handleRecordClick(item)}
                >
                    {item.id}
                </Text>
            ),
        });

        cols.push({
            title: I18n.get('Product Code'),
            width: '1fr',
            render: (item: Registration) => item.productCode,
        });

        cols.push({
            title: I18n.get('Customer Identifier'),
            width: '1fr',
            render: (item: Registration) => item.customerIdentifier,
        });

        cols.push({
            title: I18n.get('Registration AWS Account ID'),
            width: '1fr',
            render: (item: Registration) => item.customerAWSAccountId,
        });

        cols.push({
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Registration) => formatTimestamp(item.created as string),
        });

        return cols;
    };

    const selection: Record<string, Registration> = {};
    selectedRecords.forEach(r => {
        selection[r.id] = r;
    });

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumns() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumns() as TableBodyColumn[]}
                />
            </Table>

            <SimpleTablePagination
                totalPages={1}
                visibleCount={visibleItems.length}
                handlePageChange={() => fetchRecords()}
            />

            <FormDrawer
                record={selectedRecord || {} as Registration}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};

RecordsTable.propTypes = {
    extraTools: PropTypes.array,
    settings: PropTypes.object,
};
